import React, { ReactNode, SyntheticEvent } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { spacing, fontSize, borderRadius } from 'presentations/views/styles';
import { gray02, gray05, gray06 } from 'presentations/views/styles/color';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Icon from 'presentations/views/components/atoms/Icon';
import Avatar from 'presentations/views/components/atoms/Avatar';
import ErrorText from 'presentations/views/components/atoms/ErrorText';
import Tag from 'presentations/views/components/atoms/Tag';
import FormLabel from 'presentations/views/components/molecules/FormLabel';
import { Description } from 'presentations/views/components/atoms/TextField';

interface Props {
  type?: string;
  label?: string;
  description?: string | ReactNode;
  className?: string;
  placeholder?: string;
  value: any;
  size?: 'medium' | 'small';
  inputValue?: any;
  defaultValue?: string | null;
  isFullWidth?: boolean;
  isLoading?: boolean;
  width?: number;
  height?: number;
  isDisabled?: boolean;
  id?: string;
  name?: string;
  errors?: string[];
  options: any[];
  isSelect?: boolean;
  helperText?: string;
  rows?: number;
  maxRows?: number;
  isMultiline?: boolean;
  isMultiple?: boolean;
  children?: ReactNode;
  onChange?: any;
  onDelete?: (uid: string) => void;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: AutoCompleteOption,
    extra: any,
  ) => ReactNode;
  filterOptions?: any;
  onKeyDown?: any;
  onInputChange?: any;
  getOptionLabel?: any;
  isRequired?: boolean;
  isNotRequired?: boolean;
  inputAutoComplete?: string;
  isFreeSolo?: boolean;
  variant?: 'outlined' | 'standard' | 'filled';
}

export interface AutoCompleteOption {
  title: string;
  uid: string;
  image?: string | null;
  icon?: string | null;
}

export default function AutoCompleteTextField(props: Props) {
  const errors = props.errors ? props.errors : [];

  const onChange = (
    e: SyntheticEvent<any>,
    eventValue: any,
    eventReason: string,
  ) => {
    props.onChange(e, eventValue, eventReason);
  };

  let description = null;
  if (props.description) {
    description = <Description>{props.description}</Description>;
  }

  const isError = errors.length > 0;
  return (
    <>
      {props.label && (
        <FormLabel
          isRequired={props.isRequired}
          isNotRequired={props.isNotRequired}
        >
          {props.label}
        </FormLabel>
      )}
      {description}
      <Autocomplete
        multiple={props.isMultiple}
        onKeyDown={props.onKeyDown}
        onInputChange={props.onInputChange}
        value={props.value ?? null}
        options={props.options}
        inputValue={props.inputValue}
        disabled={props.isDisabled || props.isLoading}
        autoHighlight
        filterOptions={props.filterOptions}
        renderTags={(rows: AutoCompleteOption[], getTagProps: any) => {
          return rows.map((row, index) => (
            <Tag
              key={index}
              variant="outlined"
              label={row.title}
              avatar={row.image && <Avatar alt={row.title} src={row.image} />}
              deleteIcon={
                <span
                  onClick={() => {
                    props.onDelete && props.onDelete(row.uid);
                  }}
                >
                  <Icon name="clear" />
                </span>
              }
              {...getTagProps({ index })}
            />
          ));
        }}
        onChange={onChange}
        renderOption={props.renderOption}
        getOptionLabel={
          props.getOptionLabel ||
          ((option: any) => {
            if (option.title) {
              return option.title;
            }

            return '';
          })
        }
        freeSolo={props.isFreeSolo === false ? false : true}
        renderInput={(params) => {
          return (
            <StyledTextField
              {...params}
              type={props.type || 'text'}
              placeholder={props.placeholder}
              disabled={props.isDisabled || props.isLoading}
              InputLabelProps={{
                shrink: false,
              }}
              InputProps={
                props.isFreeSolo === false
                  ? {
                      ...params.InputProps,
                    }
                  : {
                      ...params.InputProps,
                      endAdornment: null,
                    }
              }
              className={props.className}
              variant={props.variant ?? 'outlined'}
              defaultValue={props.defaultValue}
              fullWidth={props.isFullWidth}
              width={props.width}
              height={props.height}
              error={isError}
              name={props.name}
              rows={props.rows}
              maxRows={props.maxRows}
              size={props.size || 'medium'}
              multiline={props.isMultiline}
              // onChange={props.onChange}
              inputProps={{
                ...params.inputProps,
                autoComplete: props.inputAutoComplete || 'new-password',
              }}
            >
              {props.children}
            </StyledTextField>
          );
        }}
      />
      {props.helperText && (
        <HelperText dangerouslySetInnerHTML={{ __html: props.helperText }} />
      )}
      {isError &&
        errors.map((errorText, i) => (
          <ErrorText key={i}>{errorText}</ErrorText>
        ))}
    </>
  );
}

const StyledTextField = styled(TextField)<{
  width: number | undefined;
  height: number | undefined;
}>`
  width: ${(props) =>
    props.width &&
    css`
      ${props.width}px;
    `};
  height: ${(props) =>
    props.height &&
    css`
      ${props.height}px;
    `};
  .MuiInputBase-root {
    border-radius: ${borderRadius(6)};
    font-size: ${fontSize(14)};
  }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border: 1px solid ${gray02};
  }

  .MuiFilledInput-root {
    background-color: ${gray06};
    font-size: ${fontSize(14)};
  }
  .MuiAutocomplete-inputRoot.MuiFilledInput-root {
    padding: ${spacing(8)};
  }
  .MuiFilledInput-root:hover {
    background-color: ${gray05};
  }
  .MuiAutocomplete-inputRoot[class*='MuiFilledInput-root']
    .MuiAutocomplete-input {
    padding: 0;
  }
  .MuiFilledInput-underline:before {
    border: none;
  }
  .MuiFilledInput-underline:after {
    border: none;
  }
`;
const HelperText = styled.div`
  font-size: ${fontSize(12)};
  margin-top: ${spacing(12)};
`;
